/**
 * ThemePreview — a live miniature of the app, for the theme customiser.
 *
 * The customiser used to be nineteen swatches with names like "Raised
 * cards". You could see the colour and read the label and still not know
 * where it landed, so tuning a theme meant changing something, closing
 * the dialog, looking, and going back.
 *
 * This is that loop collapsed into one view. It is NOT a screenshot: it
 * is built from the same CSS variables the app is, so it repaints the
 * instant a swatch changes — no capture, nothing to keep in sync, and it
 * is honest by construction.
 *
 * It mirrors the OVERVIEW specifically — top bar with its search field
 * and icon buttons, the left dock with a selected row, the hero widgets,
 * a task with its urgency stripe, the activity list, the right panel —
 * because that is the page people actually look at while tuning. A
 * preview of a generic card and a generic button teaches nothing about
 * the page you are trying to fix.
 *
 * Every part carries `data-tk`, a space-separated list of the tokens it
 * uses, which drives the teaching bit: hovering a swatch lights the
 * parts that use it and dims the rest, and hovering a part lights its
 * swatch. That mapping is the thing people were missing.
 */
import './ThemePreview.css';

export default function ThemePreview({ hoverToken = null, onHoverToken }) {
  // A part is LIT when it uses the hovered token. The root gets is-dim
  // at the same time, so "everything else" recedes rather than the lit
  // part having to shout.
  const el = (tokens, cls = '') => {
    const list = tokens.split(' ');
    const lit = hoverToken && list.includes(hoverToken);
    return {
      'data-tk': tokens,
      className: `${cls}${lit ? ' is-lit' : ''}`.trim(),
      onMouseEnter: () => onHoverToken?.(list[0]),
      onMouseLeave: () => onHoverToken?.(null),
    };
  };

  return (
    <div className={`tpv${hoverToken ? ' is-dim' : ''}`} aria-hidden="true">
      {/* ── top bar: brand, search field, icon buttons, avatar ── */}
      <div {...el('--surface-toolbar', 'tpv-bar')}>
        <span {...el('--text-primary', 'tpv-brand')}>pidly</span>
        <span {...el('--surface-input --border-strong', 'tpv-search')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" />
          </svg>
          <i {...el('--text-faint', 'tpv-ph')}>Search</i>
        </span>
        <span {...el('--toolbar-icon', 'tpv-ico')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
        </span>
        <span {...el('--toolbar-icon', 'tpv-ico')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          </svg>
        </span>
        <span {...el('--accent', 'tpv-avatar')}>K</span>
      </div>

      <div className="tpv-body">
        {/* ── left dock: nav rows, one selected, one hovered ── */}
        <div {...el('--surface-panel --border-strong', 'tpv-dock')}>
          <span {...el('--text-faint', 'tpv-dock-h')}>GO TO</span>
          {/* The SELECTED row — the state people are usually trying to
              fix when they say the accent isn't showing. */}
          <span {...el('--accent-text --surface-hover', 'tpv-navrow is-on')}>
            <i className="tpv-dot" />Your Overview
          </span>
          <span {...el('--text-secondary', 'tpv-navrow')}><i className="tpv-dot" />Account</span>
          <span {...el('--text-faint', 'tpv-dock-h')}>PROJECTS</span>
          {/* Shown permanently in its hover state — otherwise the one
              token you can only see by pointing at something is the one
              you can never preview. */}
          <span {...el('--surface-hover', 'tpv-navrow is-hover')}>
            <i {...el('--accent', 'tpv-badge')}>KM</i>KCGM Mill
          </span>
          <span {...el('--text-secondary', 'tpv-navrow')}>
            <i {...el('--success', 'tpv-badge is-alt')}>TA</i>Tailings
          </span>
        </div>

        {/* ── content: heading, workspace row, task, activity ── */}
        <div className="tpv-main">
          <span {...el('--text-primary', 'tpv-h')}>KCGM Mill</span>
          <span {...el('--text-muted', 'tpv-sub')}>4 workspaces · 124 documents</span>

          <span {...el('--surface-raised --border-strong', 'tpv-card')}>
            <span className="tpv-cardrow">
              <i {...el('--accent', 'tpv-badge')}>KM</i>
              <span {...el('--text-primary', 'tpv-cardname')}>KCGM Mill</span>
              <span {...el('--success', 'tpv-here')}><i />2 here</span>
              <span {...el('--text-muted', 'tpv-num')}>88</span>
            </span>
            <span className="tpv-cardrow">
              <i {...el('--warning', 'tpv-badge is-warn')}>TA</i>
              <span {...el('--text-secondary', 'tpv-cardname')}>Tailings</span>
              <span {...el('--text-muted', 'tpv-num')}>21</span>
            </span>
          </span>

          {/* A task with its urgency stripe — the danger/warning tokens
              have somewhere real to land. */}
          <span {...el('--danger --content-lift', 'tpv-task')}>
            <span {...el('--text-primary', 'tpv-task-t')}>Fix the valve callout</span>
            <span {...el('--danger', 'tpv-due')}>Overdue 2d</span>
          </span>

          <span className="tpv-acts">
            <span className="tpv-act">
              <i {...el('--accent', 'tpv-av')}>K</i>
              <span {...el('--text-secondary', 'tpv-act-t')}>
                <b {...el('--text-primary', 'tpv-act-b')}>Kirsty</b> added a valve
              </span>
              <span {...el('--text-faint', 'tpv-when')}>3h</span>
            </span>
          </span>

          <span className="tpv-controls">
            <span {...el('--surface-control', 'tpv-btn')}>Cancel</span>
            <span {...el('--accent --accent-hover', 'tpv-btn is-accent')}>Save</span>
            <span {...el('--accent-text', 'tpv-link')}>See all 12</span>
          </span>
        </div>

        {/* ── right panel: mirrors the dock, holds the functions ── */}
        <div {...el('--surface-panel --border-strong', 'tpv-rail')}>
          <span {...el('--text-primary', 'tpv-fn')}><i className="tpv-dot" />New</span>
          <span {...el('--text-primary', 'tpv-fn')}><i className="tpv-dot" />Members</span>
          <span {...el('--border-strong', 'tpv-rule')} />
          <span {...el('--text-faint', 'tpv-dock-h')}>TASKS</span>
          <span {...el('--text-primary', 'tpv-big')}>6</span>
          <span className="tpv-bar2">
            <i {...el('--danger', 'seg')} style={{ flex: 2 }} />
            <i {...el('--warning', 'seg')} style={{ flex: 1 }} />
            <i {...el('--accent-text', 'seg')} style={{ flex: 2 }} />
          </span>
        </div>
      </div>
    </div>
  );
}
